<script lang="ts" setup>
import { Button } from '@/components/ui/button';
import type { LayoutTypes } from '@/components/ui/calendar';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from "@/lib/utils";
import { DateFormatter, getLocalTimeZone, parseDate, today, type CalendarDate } from '@internationalized/date';
import { CalendarIcon } from '@lucide/vue';
import type { DateValue } from 'reka-ui';
import type { HTMLAttributes } from 'vue';
import { computed, watch } from 'vue';


/**
 * DatePicker - A composite date picker component.
 *
 * Combines Popover + Calendar + Button following the shadcn-vue recommended pattern.
 * Supports v-model with Date objects for convenient form binding.
 *
 * @example
 * ```vue
 * <DatePicker v-model="selectedDate" placeholder="Select date" />
 * ```
 */

const props = withDefaults(
    defineProps<{
        /** The selected date value (two-way bindable) */
        modelValue?: Date;
        /** Placeholder text when no date is selected */
        placeholder?: string;
        /** Whether the date picker is disabled */
        disabled?: boolean;
        /** Calendar layout type: 'month-and-year' | 'month-only' | 'year-only' */
        layout?: LayoutTypes;
        /** Minimum selectable date */
        minDate?: Date;
        /** Maximum selectable date */
        maxDate?: Date;
        /** Additional CSS classes for the trigger button */
        class?: HTMLAttributes['class'];
        /** Button size variant */
        size?: 'default' | 'sm' | 'lg';
        /** BCP 47 locale for the visible date string and calendar (defaults to en-US) */
        locale?: string;
    }>(),
    {
        modelValue: undefined,
        placeholder: 'Pick a date',
        disabled: false,
        layout: 'month-and-year',
        minDate: undefined,
        maxDate: undefined,
        class: undefined,
        size: 'default',
        locale: undefined,
    },
);

const emit = defineEmits<{
    (e: 'update:modelValue', value: Date | undefined): void;
}>();

const resolvedLocale = computed(() => props.locale ?? 'en-US');

/**
 * Formatter for displaying the selected date.
 * Uses long month format for clarity (e.g., "January 15, 2026").
 */
const df = computed(
    () =>
        new DateFormatter(resolvedLocale.value, {
            dateStyle: 'long',
        }),
);

/** Calendar / Reka locale: prefer short tag when a region is present (e.g. fr-CA → fr) */
const calendarLocale = computed(() => {
    const tag = resolvedLocale.value;
    const [lang] = tag.split('-');
    return lang && lang.length >= 2 ? lang : tag;
});

/**
 * Convert JavaScript Date to @internationalized/date CalendarDate.
 */
function dateToCalendarDate(date: Date): CalendarDate {
    const year = date.getFullYear();
    const month = date.getMonth() + 1; // JS months are 0-indexed
    const day = date.getDate();
    return parseDate(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`);
}

/**
 * Convert @internationalized/date DateValue to JavaScript Date.
 */
function calendarDateToDate(date: DateValue): Date {
    return new Date(date.year, date.month - 1, date.day);
}

const MIN_YEAR = 1;
const MAX_YEAR = 9999;

function isYearInRange(year: number): boolean {
    return Number.isFinite(year) && year >= MIN_YEAR && year <= MAX_YEAR;
}

/**
 * Internal calendar value (CalendarDate format).
 * Syncs bidirectionally with the external modelValue (Date format).
 * Years outside 1..9999 (e.g. corrupted timestamps) are dropped silently here;
 * the watcher below logs once per transition to avoid render-loop spam.
 */
const internalValue = computed<DateValue | undefined>({
    get() {
        if (!props.modelValue) return undefined;
        if (!isYearInRange(props.modelValue.getFullYear())) return undefined;
        return dateToCalendarDate(props.modelValue);
    },
    set(newVal: DateValue | undefined) {
        emit('update:modelValue', newVal ? calendarDateToDate(newVal) : undefined);
    },
});

watch(
    () => props.modelValue,
    (val) => {
        if (val && !isYearInRange(val.getFullYear())) {
             
            console.warn('[DatePicker] modelValue year out of range, ignoring:', val);
        }
    },
    { immediate: true },
);

/**
 * Default placeholder for the calendar (today's date).
 */
const defaultPlaceholder = today(getLocalTimeZone());

/**
 * Computed min/max values for the calendar.
 */
const minValue = computed(() => (props.minDate ? dateToCalendarDate(props.minDate) : undefined));
const maxValue = computed(() => (props.maxDate ? dateToCalendarDate(props.maxDate) : undefined));

/**
 * Formatted display string for the selected date.
 */
const displayValue = computed(() => {
    if (!internalValue.value) return props.placeholder;
    return df.value.format(calendarDateToDate(internalValue.value));
});
</script>

<template>
    <Popover>
        <PopoverTrigger as-child>
            <Button
                variant="outline"
                :size="size"
                :disabled="disabled"
                :class="cn('w-[280px] justify-start text-left font-normal', !internalValue && 'text-muted-foreground', props.class)"
            >
                <CalendarIcon :class="cn('mr-2 size-4', disabled ? 'text-muted-foreground' : 'text-foreground')" />
                {{ displayValue }}
            </Button>
        </PopoverTrigger>
        <PopoverContent class="w-auto p-0" align="start">
            <Calendar
                v-model="internalValue"
                :locale="calendarLocale"
                :initial-focus="true"
                :default-placeholder="defaultPlaceholder"
                :layout="layout"
                :min-value="minValue"
                :max-value="maxValue"
            />
        </PopoverContent>
    </Popover>
</template>
